import Box from '@mui/material/Box';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import Checkbox from '@mui/material/Checkbox';
import CircularProgress from '@mui/material/CircularProgress';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { useTheme } from '@mui/material/styles';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import {
    normalizePriceBookEntityType,
    type PriceBookEntityType,
} from 'src/api/pimApi';

export type CopyPriceItem = {
    key: string;
    productId: string;
    entityType: PriceBookEntityType;
    name: string;
    sku: string;
    catalogueId: string;
    categoryId: string;
    monthlyMsrp: string;
    oneYearMsrp: string;
    threeYearMsrp: string;
    minMonthlyMsrp: number | null;
    minOneYearMsrp: number | null;
    showMonthly: boolean;
    showOneYear: boolean;
    selected: boolean;
};

const toMsrpString = (value: unknown) => {
    if (typeof value === 'string' || typeof value === 'number') return String(value);
    return '';
};

const toMsrpNumber = (value: string): number | null => {
    if (!value.trim()) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
};

export const getCopyItemMsrpError = (
    item: CopyPriceItem,
    field: 'monthlyMsrp' | 'oneYearMsrp'
): string => {
    if (!item.selected) return '';
    const isMonthly = field === 'monthlyMsrp';
    if (isMonthly && !item.showMonthly) return '';
    if (!isMonthly && !item.showOneYear) return '';

    const value = isMonthly ? item.monthlyMsrp : item.oneYearMsrp;
    const min = isMonthly ? item.minMonthlyMsrp : item.minOneYearMsrp;
    const parsed = toMsrpNumber(value);

    if (parsed === null) return 'Enter a valid price';
    if (min !== null && parsed < min) {
        return `Must be at least ${min}`;
    }
    return '';
};

export const hasInvalidCopyPrices = (items: CopyPriceItem[]): boolean =>
    items.some(
        (item) =>
            Boolean(getCopyItemMsrpError(item, 'monthlyMsrp')) ||
            Boolean(getCopyItemMsrpError(item, 'oneYearMsrp'))
    );

export const mapPricebooksToCopyItems = (rows: unknown[]): CopyPriceItem[] =>
    (Array.isArray(rows) ? rows : [])
        .map((raw) => {
            const item = (raw ?? {}) as Record<string, any>;
            const product = item.product ?? {};
            const productId = String(
                item.productId ?? product?._id ?? product?.id ?? ''
            );
            if (!productId) return null;

            const monthlyMsrp = toMsrpString(item.monthlyMsrp);
            const oneYearMsrp = toMsrpString(item.oneYearMsrp);
            const hasMonthly = monthlyMsrp !== '';
            const hasOneYear = oneYearMsrp !== '';

            return {
                key: String(
                    item._id ??
                        `${normalizePriceBookEntityType(item.entityType)}-${productId}`
                ),
                productId,
                entityType: normalizePriceBookEntityType(item.entityType),
                name: String(product?.Name ?? product?.name ?? 'Untitled'),
                sku: String(product?.SKU ?? product?.sku ?? ''),
                catalogueId: String(
                    item.catalogueId ?? item.productCatalogues?._id ?? ''
                ),
                categoryId: String(
                    item.categoryId ?? item.productCategories?._id ?? ''
                ),
                monthlyMsrp,
                oneYearMsrp,
                threeYearMsrp: toMsrpString(item.threeYearMsrp),
                minMonthlyMsrp: hasMonthly ? toMsrpNumber(monthlyMsrp) : null,
                minOneYearMsrp: hasOneYear ? toMsrpNumber(oneYearMsrp) : null,
                showMonthly: hasMonthly || !hasOneYear,
                showOneYear: hasOneYear || !hasMonthly,
                selected: true,
            };
        })
        .filter(Boolean) as CopyPriceItem[];

export const copyItemIdentity = (item: Pick<CopyPriceItem, 'entityType' | 'productId'>) =>
    `${item.entityType}:${item.productId}`;

export const toCopyPriceBookPayload = (item: CopyPriceItem) => ({
    catalogueId: item.catalogueId,
    categoryId: item.categoryId,
    productId: item.productId,
    entityType: item.entityType,
    monthlyMsrp: item.showMonthly ? item.monthlyMsrp : '',
    oneYearMsrp: item.showOneYear ? item.oneYearMsrp : '',
    threeYearMsrp: item.threeYearMsrp,
    isActive: true,
});

const CountBadge = ({ count }: { count: number }) => {
    const theme = useTheme();

    return (
        <Box
            component="span"
            style={{
                minWidth: 22,
                height: 20,
                padding: '0 6px',
                borderRadius: 10,
                backgroundColor: theme.palette.grey[200],
                color: theme.palette.text.secondary,
                fontSize: 12,
                fontWeight: 600,
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
            }}>
            {count}
        </Box>
    );
};

const CopyItemRow = ({
    item,
    onToggle,
    onMsrpChange,
    showCheckbox = true,
    msrpReadOnly = false,
}: {
    item: CopyPriceItem;
    onToggle?: (key: string) => void;
    onMsrpChange?: (key: string, field: 'monthlyMsrp' | 'oneYearMsrp', value: string) => void;
    showCheckbox?: boolean;
    msrpReadOnly?: boolean;
}) => {
    const theme = useTheme();
    const monthlyError = getCopyItemMsrpError(item, 'monthlyMsrp');
    const oneYearError = getCopyItemMsrpError(item, 'oneYearMsrp');

    return (
        <Box
            display="flex"
            alignItems="flex-start"
            style={{
                gap: 12,
                backgroundColor: theme.palette.grey[50],
                border: `1px solid ${theme.palette.divider}`,
                borderRadius: 8,
                padding: '10px 14px',
            }}>
            {showCheckbox && (
            <Checkbox
                checked={item.selected}
                onChange={() => onToggle?.(item.key)}
                color="primary"
                size="small"
                sx={{ mt: 0.5 }}
            />
            )}
            <Typography
                style={{
                    flex: 1,
                    minWidth: 140,
                    fontSize: 14,
                    fontWeight: 700,
                    color: theme.palette.text.primary,
                    paddingTop: 8,
                }}>
                {item.name}
            </Typography>
            <Box display="flex" alignItems="flex-start" style={{ gap: 12 }}>
                {item.showMonthly && (
                    <TextField
                        label="Monthly MSRP"
                        size="small"
                        value={item.monthlyMsrp}
                        disabled={msrpReadOnly || !item.selected}
                        error={!msrpReadOnly && Boolean(monthlyError)}
                        helperText={!msrpReadOnly ? monthlyError || undefined : undefined}
                        onChange={(event) =>
                            onMsrpChange?.(item.key, 'monthlyMsrp', event.target.value)
                        }
                        sx={{ width: 130 }}
                    />
                )}
                {item.showOneYear && (
                    <TextField
                        label="1-Year MSRP"
                        size="small"
                        value={item.oneYearMsrp}
                        disabled={msrpReadOnly || !item.selected}
                        error={!msrpReadOnly && Boolean(oneYearError)}
                        helperText={!msrpReadOnly ? oneYearError || undefined : undefined}
                        onChange={(event) =>
                            onMsrpChange?.(item.key, 'oneYearMsrp', event.target.value)
                        }
                        sx={{ width: 130 }}
                    />
                )}
            </Box>
            <Typography
                title={item.sku}
                style={{
                    width: 180,
                    fontSize: 12,
                    fontStyle: 'italic',
                    color: theme.palette.text.disabled,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    whiteSpace: 'nowrap',
                    textAlign: 'right',
                    paddingTop: 8,
                }}>
                {item.sku}
            </Typography>
        </Box>
    );
};

const CopySection = ({
    title,
    items,
    onToggle,
    onMsrpChange,
    emptySuffix = 'to copy',
    showCheckbox = true,
    msrpReadOnly = false,
    showEmpty = true,
}: {
    title: string;
    items: CopyPriceItem[];
    onToggle?: (key: string) => void;
    onMsrpChange?: (key: string, field: 'monthlyMsrp' | 'oneYearMsrp', value: string) => void;
    emptySuffix?: string;
    showCheckbox?: boolean;
    msrpReadOnly?: boolean;
    showEmpty?: boolean;
}) => {
    const theme = useTheme();

    if (!showEmpty && items.length === 0) return null;

    return (
        <Box marginBottom={2.5}>
            <Box
                display="flex"
                alignItems="center"
                style={{ gap: 8, marginBottom: 10 }}>
                <Typography
                    style={{
                        fontSize: 15,
                        fontWeight: 700,
                        color: theme.palette.text.primary,
                    }}>
                    {title}
                </Typography>
                {showCheckbox && <CountBadge count={items.length} />}
            </Box>
            {items.length === 0 ? (
                <Typography
                    style={{
                        fontSize: 13,
                        color: theme.palette.text.secondary,
                        padding: '4px 0 8px',
                    }}>
                    No {title.toLowerCase()} {emptySuffix}
                </Typography>
            ) : (
                <Box display="flex" flexDirection="column" style={{ gap: 10 }}>
                    {items.map((item) => (
                        <CopyItemRow
                            key={item.key}
                            item={item}
                            onToggle={onToggle}
                            onMsrpChange={onMsrpChange}
                            showCheckbox={showCheckbox}
                            msrpReadOnly={msrpReadOnly}
                        />
                    ))}
                </Box>
            )}
        </Box>
    );
};

interface CopyProductsAndPackagesStepProps {
    items: CopyPriceItem[];
    loading: boolean;
    onToggle: (key: string) => void;
    onMsrpChange: (key: string, field: 'monthlyMsrp' | 'oneYearMsrp', value: string) => void;
    subtitle?: string;
    emptySuffix?: string;
    existingItems?: CopyPriceItem[];
}

export const CopyProductsAndPackagesStep = ({
    items,
    loading,
    onToggle,
    onMsrpChange,
    subtitle = 'Select packages and products to copy to this reseller',
    emptySuffix = 'to copy',
    existingItems,
}: CopyProductsAndPackagesStepProps) => {
    const theme = useTheme();
    const packages = items.filter((item) => item.entityType === 'PACKAGE');
    const products = items.filter((item) => item.entityType === 'PRODUCT');
    const existingPackages = (existingItems || []).filter(
        (item) => item.entityType === 'PACKAGE'
    );
    const existingProducts = (existingItems || []).filter(
        (item) => item.entityType === 'PRODUCT'
    );

    if (loading) {
        return (
            <Box display="flex" justifyContent="center" paddingY={4}>
                <CircularProgress size={28} />
            </Box>
        );
    }

    return (
        <Box>
            {subtitle && (
                <Typography
                    style={{
                        fontSize: 13,
                        color: theme.palette.text.secondary,
                        marginBottom: 20,
                        marginTop: -8,
                    }}>
                    {subtitle}
                </Typography>
            )}
            <CopySection
                title="Packages"
                items={packages}
                onToggle={onToggle}
                onMsrpChange={onMsrpChange}
                emptySuffix={emptySuffix}
            />
            <CopySection
                title="Products"
                items={products}
                onToggle={onToggle}
                onMsrpChange={onMsrpChange}
                emptySuffix={emptySuffix}
            />
            {existingItems && (
                <Accordion
                    disableGutters
                    elevation={0}
                    sx={{
                        mt: 1,
                        '&:before': { display: 'none' },
                        border: `1px solid ${theme.palette.divider}`,
                        borderRadius: '8px !important',
                        overflow: 'hidden',
                    }}>
                    <AccordionSummary
                        expandIcon={<ExpandMoreIcon />}
                        sx={{ minHeight: 48, px: 2 }}>
                        <Box display="flex" alignItems="center" style={{ gap: 8 }}>
                            <Typography
                                style={{
                                    fontSize: 15,
                                    fontWeight: 700,
                                    color: theme.palette.text.primary,
                                }}>
                                Already in price book
                            </Typography>
                            <CountBadge count={existingItems.length} />
                        </Box>
                    </AccordionSummary>
                    <AccordionDetails sx={{ px: 2, pb: 2, pt: 0 }}>
                        <CopySection
                            title="Packages"
                            items={existingPackages}
                            showCheckbox={false}
                            msrpReadOnly
                            showEmpty={false}
                        />
                        <CopySection
                            title="Products"
                            items={existingProducts}
                            showCheckbox={false}
                            msrpReadOnly
                            showEmpty={false}
                        />
                    </AccordionDetails>
                </Accordion>
            )}
        </Box>
    );
};
